// Content for the /start guided onboarding: questions, motivation-tailored
// showcase cards, and the live-demo verses. Copy lives here so tuning the
// funnel never touches component logic.

// Opening hook quiz: three Quranic words the target visitor (reads script,
// hears these in ṣalāh) can plausibly guess — the funnel opens by letting
// them prove to themselves they can already read the Quran's words.
// Choices are authored CORRECT-FIRST (project convention); the component
// shuffles at render via useShuffledChoices. Frequency copy is deliberately
// unspecific ("hundreds of times") — exact counts pending corpus check.
export const HOOK_QUIZ = [
  {
    key: "kitab",
    audioKey: "qz_kitab",
    arabic: "كِتَاب",
    translit: "kitāb",
    choices: ["book", "door", "mountain", "night"],
    fact: "It's in the Quran's second verse — ذَٰلِكَ الْكِتَابُ — and hundreds of places after.",
    encourage: "Now you know it — and you'll meet it on almost every page.",
  },
  {
    key: "rahmah",
    audioKey: "qz_rahmah",
    arabic: "رَحْمَة",
    translit: "raḥmah",
    choices: ["mercy", "anger", "wealth", "journey"],
    fact: "You recite its root every day — الرَّحْمَٰنِ الرَّحِيمِ.",
    encourage: "You already recite its root daily — الرَّحْمَٰنِ الرَّحِيمِ.",
  },
  {
    key: "yawm",
    audioKey: "qz_yawm",
    arabic: "يَوْم",
    translit: "yawm",
    choices: ["day", "night", "week", "sun"],
    fact: "As in يَوْمِ الدِّينِ — one of the Quran's most frequent nouns.",
    encourage: "As in يَوْمِ الدِّينِ — you'll own this one in your first week.",
  },
];

export const QUESTIONS = [
  {
    key: "motivation",
    title: "What brings you to Arabic?",
    subtitle: "We'll shape your path around it.",
    options: [
      {
        value: "quran",
        label: "Understand the Quran",
        description: "Read the revelation without a translation in the way",
        arabic: "لِتَفْهَمَ القُرْآن",
      },
      {
        value: "salah",
        label: "Presence in prayer",
        description: "Know what you're saying in ṣalāh and duʿāʼ",
        arabic: "خُشُوعٌ فِي الصَّلَاة",
      },
      {
        value: "classical_texts",
        label: "Read classical texts",
        description: "Open the books of the scholars in their own words",
        arabic: "كُتُبُ العُلَمَاء",
      },
      {
        value: "speaking",
        label: "Speak Arabic",
        description: "Hold a real conversation in fuṣḥā",
        arabic: "تَكَلَّمِ العَرَبِيَّة",
      },
    ],
  },
  {
    key: "script_level",
    title: "Can you read Arabic script?",
    subtitle: "Be honest — it just sets your starting point.",
    options: [
      {
        value: "fluent",
        label: "I read it — I just don't understand it",
        description: "You're exactly who this app was built for",
      },
      {
        value: "slow",
        label: "Slowly, with effort",
        description: "Voice-over on every sentence will carry you",
      },
      {
        value: "none",
        label: "Not yet",
        description: "Murājaʿa assumes basic reading — you can still explore",
      },
    ],
  },
  {
    key: "daily_minutes",
    title: "How many minutes a day?",
    subtitle: "Small and daily beats long and rare. However long it takes.",
    options: [
      { value: 5, label: "5 minutes", description: "A few words after ṣalāh" },
      { value: 10, label: "10 minutes", description: "One focused step a day" },
      { value: 15, label: "15 minutes", description: "A lesson step plus review" },
      { value: 30, label: "30 minutes", description: "Serious, steady progress" },
    ],
  },
];

export const MOTIVATION_LABELS = {
  quran: "understanding the Quran",
  salah: "presence in prayer",
  classical_texts: "reading classical texts",
  speaking: "speaking Arabic",
};

// Showcase decks by motivation. Feature copy is descriptive only — no
// deep links, since flag-gated surfaces can't be checked anonymously.
export const SHOWCASES = {
  quran: [
    {
      icon: "🕌",
      title: "The Quran's own vocabulary",
      body: "A curriculum built from the most frequent Quranic words — covering roughly 85% of the word tokens you'll meet on any page.",
    },
    {
      icon: "◇",
      title: "Tap any word, see inside it",
      body: "Every Arabic sentence is clickable: instant translation, voice-over, and the full word structure — root, prefixes, case endings, iʿrāb.",
    },
    {
      icon: "↻",
      title: "Review that never lets go",
      body: "Words you learn come back for review right before you'd forget them, and everything you save lands in your Word Bank.",
    },
  ],
  salah: [
    {
      icon: "🕌",
      title: "Understand what you recite",
      body: "Learn the highest-frequency Quranic words first — the vocabulary of the sūrahs you already recite every day.",
    },
    {
      icon: "◇",
      title: "Tap any word, see inside it",
      body: "Every sentence is clickable: instant translation, voice-over, and the full word structure down to the iʿrāb.",
    },
    {
      icon: "↻",
      title: "A little every day",
      body: "Daily review brings words back right before you'd forget them — built for steady, unhurried progress.",
    },
  ],
  classical_texts: [
    {
      icon: "📖",
      title: "Bring your own books",
      body: "Upload any Arabic PDF. Tap a word on the scanned page for translation and its full morphological breakdown.",
    },
    {
      icon: "◇",
      title: "Grammar you can see",
      body: "Highlight the verbs, nouns, pronouns and particles in any sentence with one tap — then open any word's iʿrāb.",
    },
    {
      icon: "↻",
      title: "Your personal word bank",
      body: "Save words and whole sentences as you read; the app drills you on them until they stick.",
    },
  ],
  speaking: [
    {
      icon: "💬",
      title: "40+ speaking topics",
      body: "Guided lessons that walk you from vocabulary to listening to a real role-play conversation — with voice-over on every line.",
    },
    {
      icon: "🤝",
      title: "Practice without the fear",
      body: "Not ready to speak to people? Chat with the built-in AI in fuṣḥā first, with a helper that turns your English into Arabic phrasings.",
    },
    {
      icon: "◇",
      title: "Understand what you say",
      body: "Every word in every sentence is tappable — translation, voice-over, and grammar structure on demand.",
    },
  ],
};

// Live-demo verses: short āyāt from the built-in Quran curriculum, so the
// morphology cache is already warm (analyze = cache hit, no AI spend).
// IMPORTANT: text must match the prewarmed MorphologyCache.sentence for the
// verse_key byte-for-byte (Uthmani waṣla-alif ٱ included) — the cache is
// keyed on the exact diacritized sentence, and a plain-alif spelling misses
// the AI-refined entries and gets served provisional local analyses instead.
export const DEMO_VERSES = [
  { key: "112:1", ref: "Al-Ikhlāṣ 112:1", text: "قُلْ هُوَ ٱللَّهُ أَحَدٌ" },
  { key: "1:5", ref: "Al-Fātiḥah 1:5", text: "إِيَّاكَ نَعْبُدُ وَإِيَّاكَ نَسْتَعِينُ" },
  { key: "103:1", ref: "Al-ʿAṣr 103:1", text: "وَٱلْعَصْرِ" },
];
